import React, { useState, useEffect } from "react";

// Shared month-grid scheduler used by both the intake form and the reschedule flow. Availability is
// fetched by the parent and passed in as `locations`; the parent owns the authoritative booking on
// submit. `onSelect({ id, locationName, address, day, time })` fires when a time is chosen (null when
// cleared). Lives in a subfolder so webpack's per-*.jsx entry glob doesn't treat it as an entry.
export const CAL_WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
export const CAL_MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
export const pad2 = (n) => String(n).padStart(2, "0");

export function Scheduler({ locations, selectedId, onSelect, disabled }) {
    const [locIndex, setLocIndex] = useState(0);
    const [view, setView] = useState(null);          // { year, month } — month is 0-11
    const [selectedIso, setSelectedIso] = useState(null);

    const loc = locations.length ? locations[Math.min(locIndex, locations.length - 1)] : null;
    const days = (loc && loc.days) || [];
    const firstIso = days.length ? days.map((d) => d.isoDate).sort()[0] : null;

    // Open the calendar on the first month that has availability (and when the location changes).
    useEffect(() => {
        if (firstIso) {
            const [y, m] = firstIso.split("-").map(Number);
            setView({ year: y, month: m - 1 });
            setSelectedIso(null);
        } else {
            setView(null);
            setSelectedIso(null);
        }
    }, [firstIso, locIndex]);

    if (!loc) {
        return <p className="ai-note-empty">No appointment times are currently available. Please check back later.</p>;
    }

    const dayByIso = {};
    days.forEach((d) => { dayByIso[d.isoDate] = d; });
    const openIsos = Object.keys(dayByIso).sort();

    const locationField = (
        <div className="ai-field ai-field--full">
            <label className="ai-field__label" htmlFor="ai-location">
                Enrollment Location<span className="ai-req">*</span>
            </label>
            <select id="ai-location" value={locIndex} disabled={disabled}
                onChange={(e) => { setLocIndex(Number(e.target.value)); onSelect(null); }}>
                {locations.map((l, i) => (
                    <option key={l.locationId} value={i}>
                        {l.name}{l.address ? " — " + l.address : ""}
                    </option>
                ))}
            </select>
        </div>
    );

    if (!openIsos.length || !view) {
        return (
            <>
                {locationField}
                <p className="ai-note-empty">No times available at this location.</p>
            </>
        );
    }

    const monthIndex = (iso) => { const [y, m] = iso.split("-").map(Number); return y * 12 + (m - 1); };
    const minMonth = monthIndex(openIsos[0]);
    const maxMonth = monthIndex(openIsos[openIsos.length - 1]);
    const curMonth = view.year * 12 + view.month;
    const step = (delta) => {
        const next = curMonth + delta;
        if (next >= minMonth && next <= maxMonth) setView({ year: Math.floor(next / 12), month: next % 12 });
    };

    const firstWeekday = new Date(view.year, view.month, 1).getDay(); // 0 = Sunday
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const iso = view.year + "-" + pad2(view.month + 1) + "-" + pad2(d);
        cells.push({ d, iso, open: Object.prototype.hasOwnProperty.call(dayByIso, iso) });
    }

    const selectedDay = selectedIso ? dayByIso[selectedIso] : null;

    return (
        <>
            {locationField}

            <div className="cal">
                <div className="cal__panel">
                    <div className="cal__month">
                        <button type="button" className="cal__nav" onClick={() => step(-1)}
                            disabled={disabled || curMonth <= minMonth} aria-label="Previous month">‹</button>
                        <span className="cal__monthlabel">{CAL_MONTHS[view.month]} {view.year}</span>
                        <button type="button" className="cal__nav" onClick={() => step(1)}
                            disabled={disabled || curMonth >= maxMonth} aria-label="Next month">›</button>
                    </div>
                    <div className="cal__grid" role="grid">
                        {CAL_WEEKDAYS.map((w) => <div key={w} className="cal__weekday">{w}</div>)}
                        {cells.map((c, i) => (c === null
                            ? <div key={"b" + i} className="cal__cell is-empty" />
                            : (
                                <button type="button" key={c.iso}
                                    className={"cal__cell" + (c.open ? " is-open" : "") + (c.iso === selectedIso ? " is-selected" : "")}
                                    disabled={disabled || !c.open} aria-pressed={c.iso === selectedIso}
                                    onClick={() => {
                                        if (c.iso === selectedIso) return;
                                        // Changing the day clears any time chosen on the previous day, so a
                                        // stale slot can't be booked while the new day shows no selection
                                        // (mirrors the location-change reset above). SCRUM-1199.
                                        setSelectedIso(c.iso);
                                        onSelect(null);
                                    }}>
                                    {c.d}
                                </button>
                            )))}
                    </div>
                </div>

                <div className="cal__times">
                    {selectedDay ? (
                        <>
                            <div className="ai-group-label">Available times — {selectedDay.date}</div>
                            <div className="cal__slots">
                                {selectedDay.slots.map((slot) => {
                                    const full = slot.status === "Full";
                                    const selected = slot.slotId === selectedId;
                                    const cls = "cal__slot" + (full ? " is-full" : "")
                                        + (selected ? " is-selected" : "") + (slot.status === "Limited" ? " is-limited" : "");
                                    return (
                                        <button type="button" key={slot.slotId} className={cls} disabled={disabled || full}
                                            aria-pressed={selected} title={slot.status}
                                            onClick={() => onSelect({ id: slot.slotId, locationName: loc.name, address: loc.address, day: selectedDay.date, time: slot.time })}>
                                            <span className="cal__time">{slot.time}</span>
                                            {slot.status === "Limited" ? <span className="cal__badge">Limited</span> : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <p className="cal__prompt">Select a date to see available times.</p>
                    )}
                </div>
            </div>
        </>
    );
}
